import { Injectable, signal, computed, inject } from '@angular/core';
import {
  CompanyData,
  CompanyConfig,
  DynamicCompanyResponse,
  CompanyApplication,
  DynamicCompanyTokens,
  CompanyTokenData
} from '../models/dynamic-company.models';
import { AuthStateService } from './auth-state.service';
import { StorageIntegrityService } from './storage-integrity.service';

@Injectable({
  providedIn: 'root'
})
export class CompanyConfigService {
  private readonly authStateService = inject(AuthStateService);
  private readonly storageIntegrityService = inject(StorageIntegrityService);

  // Available companies from API response
  private availableCompanies = signal<string[]>([]);
  private companyConfigs = signal<Record<string, CompanyConfig>>({});
  private applications = signal<CompanyApplication[]>([]);

  // Computed values
  readonly companies = computed(() => this.availableCompanies());
  readonly configs = computed(() => this.companyConfigs());
  readonly hasMultipleCompanies = computed(() => this.companies().length > 1);
  readonly activeCompanyConfig = computed(() => {
    const currentCompany = this.authStateService.currentCompany();
    return currentCompany ? this.companyConfigs()[currentCompany] : null;
  });

  constructor() {
    this.restoreFromStorage();
  }

  /**
   * Restore available companies and partial configs from localStorage on startup.
   * This allows company switching to work after a full page refresh when
   * in-memory signals have been reset.
   */
  private restoreFromStorage(): void {
    try {
      const applicationsJson = localStorage.getItem('Applications');
      if (!applicationsJson) return;

      const apps: CompanyApplication[] = JSON.parse(applicationsJson);
      this.applications.set(apps);

      const availableNames = apps.filter(a => a.isAvailable).map(a => a.name);
      this.availableCompanies.set(availableNames);

      // Rebuild partial configs from stored user details (tokens are memory-only)
      const configs: Record<string, CompanyConfig> = {};
      availableNames.forEach(companyName => {
        const userDetailsJson = localStorage.getItem(`tibss.${companyName}-userDetails`);
        if (userDetailsJson) {
          try {
            const user = JSON.parse(userDetailsJson);
            const app = apps.find(a => a.name === companyName);
            configs[companyName] = {
              name: companyName,
              displayName: app?.displayName || companyName.toUpperCase(),
              baseUrl: app?.BaseUrl || '',
              apiHost: app?.BaseUrl || '',
              token: '',
              user,
              logo: app?.logo || '',
              icon: app?.icon || '',
              isActive: true,
              lastUsed: new Date()
            };
          } catch {
            // ignore malformed entry
          }
        }
      });

      if (Object.keys(configs).length > 0) {
        this.companyConfigs.set(configs);
      }
    } catch (error) {
      console.error('[CompanyConfigService] Error restoring from storage:', error);
    }
  }

  /**
   * Process dynamic company response from login API
   */
  processCompanyResponse(response: DynamicCompanyResponse, availableCompanies: string[]): void {
    console.log('Processing dynamic company response:', response);

    // Set available companies
    this.availableCompanies.set(availableCompanies);

    // Process each company in the response
    const configs: Record<string, CompanyConfig> = {};
    const apps: CompanyApplication[] = [];
    const tokens: DynamicCompanyTokens = {};

    availableCompanies.forEach(companyName => {
      const companyData = response[companyName];

      if (companyData && companyData.Status) {
        // Create company config
        configs[companyName] = this.createCompanyConfig(companyName, companyData);

        // Create application entry
        apps.push(this.createApplicationEntry(companyName, companyData));

        // Create token data
        tokens[companyName] = this.createTokenData(companyData);

        // Store company-specific data in localStorage
        this.storeCompanyData(companyName, companyData);

        console.log(`Processed company: ${companyName}`, configs[companyName]);
      } else {
        console.warn(`Company ${companyName} data is invalid or inactive`);
      }
    });

    // Update signals
    this.companyConfigs.set(configs);
    this.applications.set(apps);

    // Update auth state with tokens
    this.authStateService.setCompanyTokens(tokens);

    // Store applications in localStorage for compatibility
    localStorage.setItem('Applications', JSON.stringify(apps));

    console.log('Dynamic company processing complete', {
      companies: availableCompanies,
      configs: Object.keys(configs),
      applications: apps.length
    });
  }

  /**
   * Create company configuration object
   */
  private createCompanyConfig(companyName: string, data: CompanyData): CompanyConfig {
    const userData = data.data[0];

    return {
      name: companyName,
      displayName: data.AppName || companyName.toUpperCase(),
      baseUrl: `${data.host}/`,
      apiHost: data.host,
      token: data.Token,
      refreshToken: data.refreshToken,
      user: userData,
      logo: data.logo,
      icon: data.icon,
      isActive: data.Access,
      lastUsed: new Date()
    };
  }

  /**
   * Create application entry for compatibility
   */
  private createApplicationEntry(companyName: string, data: CompanyData): CompanyApplication {
    return {
      name: companyName,
      displayName: data.AppName || companyName.toUpperCase(),
      BaseUrl: `${data.host}/`,
      icon: data.icon,
      logo: data.logo,
      isAvailable: data.Access
    };
  }

  /**
   * Create token data for auth state
   */
  private createTokenData(data: CompanyData): CompanyTokenData {
    const userData = data.data[0];
    const tokenExpiryFromJwt = this.parseJwtExpiry(data.Token);

    return {
      token: data.Token,
      userId: userData.userID,
      // Prefer JWT `exp` to avoid backend datetime parsing issues.
      expiresAt: tokenExpiryFromJwt || new Date(Date.now() + 30 * 60 * 1000)
    };
  }

  private parseJwtExpiry(token: string): Date | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }

      const payload = JSON.parse(this.base64UrlDecode(parts[1])) as { exp?: number };
      if (!payload.exp || typeof payload.exp !== 'number') {
        return null;
      }

      return new Date(payload.exp * 1000);
    } catch {
      return null;
    }
  }

  private base64UrlDecode(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
    return atob(normalized + padding);
  }

  /**
   * Store company-specific data in localStorage - matches old system format
   */
  private storeCompanyData(companyName: string, data: CompanyData): void {
    const userData = data.data[0];

    // Store only non-sensitive company metadata in browser storage.
    // Access and refresh tokens are intentionally kept in-memory.
    localStorage.setItem(`tibss.${companyName}-userDetails`, JSON.stringify(userData));

    // Store the API access key so permission requests can use the correct companyId.
    if (data.access_key) {
      localStorage.setItem(`tibss.${companyName}-accessKey`, data.access_key);
    }
  }

  /**
   * Parse API date string as IST (matches old system assumption)
   * API sends: "2025-09-17 03:53:50" (no timezone info)
   * Old system would treat this as local time, we assume IST
   */
  private parseApiDateAsIST(apiDateStr: string): Date {
    // Split the date string: "2025-09-17 03:53:50"
    const [datePart, timePart] = apiDateStr.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute, second] = timePart.split(":").map(Number);

    // Create date object in IST timezone
    // Note: JavaScript Date constructor uses local timezone, but we want IST
    // So we create the date and then adjust for timezone difference
    
    // Create date as if it's local time
    const localDate = new Date(year, month - 1, day, hour, minute, second);
    
    // Get IST offset (IST is UTC+5:30)
    const istOffsetMinutes = 5.5 * 60; // IST is 5.5 hours ahead of UTC
    const localOffsetMinutes = localDate.getTimezoneOffset(); // minutes behind UTC
    
    // Adjust the date to represent the same wall-clock time in IST
    const adjustmentMinutes = localOffsetMinutes + istOffsetMinutes;
    const istDate = new Date(localDate.getTime() - (adjustmentMinutes * 60 * 1000));
    
    console.log(`ðŸ“… Date conversion:`, {
      apiInput: apiDateStr,
      localDate: localDate.toString(),
      istDate: istDate.toString(),
      adjustment: `${adjustmentMinutes} minutes`,
      localOffset: localOffsetMinutes,
      istOffset: istOffsetMinutes
    });
    
    return istDate;
  }

  /**
   * Get configuration for specific company
   */
  getCompanyConfig(companyName: string): CompanyConfig | null {
    return this.companyConfigs()[companyName] || null;
  }

  /**
   * Get all available companies
   */
  getAvailableCompanies(): string[] {
    return this.availableCompanies();
  }

  /**
   * Check if company is available
   */
  isCompanyAvailable(companyName: string): boolean {
    return this.availableCompanies().includes(companyName);
  }

  /**
   * Get applications list
   */
  getApplications(): CompanyApplication[] {
    return this.applications();
  }

  /**
   * Determine active company based on preference and availability
   */
  determineActiveCompany(preferredCompany?: string): string {
    const available = this.availableCompanies();

    // If preferred company is available, use it
    if (preferredCompany && available.includes(preferredCompany)) {
      return preferredCompany;
    }

    // Otherwise, prioritize 'unity' if available
    if (available.includes('unity')) {
      return 'unity';
    }

    // Fall back to first available company
    return available[0] || 'unity';
  }

  /**
   * Switch to different company
   */
  switchCompany(companyName: string): boolean {
    if (!this.isCompanyAvailable(companyName)) {
      console.error(`Company ${companyName} is not available`);
      return false;
    }

    const config = this.getCompanyConfig(companyName);
    if (!config) {
      console.error(`Configuration for company ${companyName} not found`);
      return false;
    }

    // Update current company in auth state
    this.authStateService.setCurrentCompany(companyName);

    // Update localStorage
    localStorage.setItem('currentCompany', companyName);
    localStorage.setItem('BaseUrl', config.baseUrl);

    // Update last used timestamp
    config.lastUsed = new Date();
    this.companyConfigs.update(configs => ({
      ...configs,
      [companyName]: config
    }));

    // Re-snapshot protected keys after BaseUrl / currentCompany changed.
    this.storageIntegrityService.saveState().catch(err =>
      console.error('[StorageIntegrity] saveState failed after switchCompany:', err)
    );

    console.log(`Switched to company: ${companyName}`);
    return true;
  }

  /**
   * Get company-specific localStorage key
   */
  getCompanySpecificKey(key: string, companyName?: string): string {
    const company = companyName || this.authStateService.currentCompany() || 'unity';
    return `tibss.${company}-${key}`;
  }

  /**
   * Update company token using old system format
   */
  updateCompanyToken(companyName: string, token: string, refreshToken?: string): void {
    const config = this.getCompanyConfig(companyName);
    if (!config) return;

    config.token = token;
    if (refreshToken) {
      config.refreshToken = refreshToken;
    }

    this.companyConfigs.update(configs => ({
      ...configs,
      [companyName]: config
    }));

    const tokenData = this.authStateService.getCompanyTokenData(companyName);
    if (tokenData) {
      this.authStateService.updateCompanyToken(companyName, {
        ...tokenData,
        token
      });
    }
  }
  /**
   * Clear all company data - FULLY DYNAMIC
   */
  clearAll(): void {
    // Clear signals
    this.availableCompanies.set([]);
    this.companyConfigs.set({});
    this.applications.set([]);

    // âœ… DYNAMIC: Get companies from localStorage instead of hard-coded list
    const companies = this.getStoredCompanies();
    console.log(`Clearing data for ${companies.length} companies:`, companies);

    companies.forEach(company => {
      console.log(`  â†’ Removing data for company: ${company}`);
      localStorage.removeItem(`tibss.${company}-userDetails`);
    });

    localStorage.removeItem('Applications');
    localStorage.removeItem('currentCompany');
    localStorage.removeItem('BaseUrl');

    console.log('âœ“ Cleared all company data');
  }

  /**
   * Get company name from old storage keys for migration
   */
  getStoredCompanies(): string[] {
    const storedCompanies: string[] = [];
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      if (key.startsWith('tibss.') && key.endsWith('-userDetails')) {
        const company = key.replace('tibss.', '').replace('-userDetails', '');
        if (!storedCompanies.includes(company)) {
          storedCompanies.push(company);
        }
      }
    });

    return storedCompanies;
  }
}
